// South African payroll engine — PAYE / UIF / SDL / medical tax credits.
// Pure functions. Statutory parameters default to the 2025/2026 SARS figures
// but can be overridden by a saved config object.

export const DEFAULTS = {
  taxYear: "2025/2026",
  // Annual individual income-tax brackets. [upTo, base, rate(0-1), over]
  brackets: [
    { upTo: 237100, base: 0, rate: 0.18, over: 0 },
    { upTo: 370500, base: 42678, rate: 0.26, over: 237100 },
    { upTo: 512800, base: 77362, rate: 0.31, over: 370500 },
    { upTo: 673000, base: 121475, rate: 0.36, over: 512800 },
    { upTo: 857900, base: 179147, rate: 0.39, over: 673000 },
    { upTo: 1817000, base: 251258, rate: 0.41, over: 857900 },
    { upTo: 1000000000000, base: 644489, rate: 0.45, over: 1817000 },
  ],
  rebates: { primary: 17235, secondary: 9444, tertiary: 3145 }, // 65+, 75+
  uifRate: 0.01,        // 1% employee + 1% employer
  uifCeiling: 17712,    // remuneration cap per month for UIF
  sdlRate: 0.01,        // 1% of leviable payroll (employer)
  mtc: { main: 364, firstDep: 364, additional: 246 }, // monthly medical credits
};

export const PERIODS = { monthly: 12, fortnightly: 26, weekly: 52 };

export function round2(n) { return Math.round((Number(n) || 0) * 100) / 100; }

function cfgOf(c) {
  c = c || {};
  return {
    brackets: (c.brackets && c.brackets.length) ? c.brackets : DEFAULTS.brackets,
    rebates: c.rebates || DEFAULTS.rebates,
    uifRate: (c.uifRate != null) ? c.uifRate : DEFAULTS.uifRate,
    uifCeiling: (c.uifCeiling != null) ? c.uifCeiling : DEFAULTS.uifCeiling,
    sdlRate: (c.sdlRate != null) ? c.sdlRate : DEFAULTS.sdlRate,
    mtc: c.mtc || DEFAULTS.mtc,
    taxYear: c.taxYear || DEFAULTS.taxYear,
  };
}

export function ageFrom(dob) {
  if (!dob) return 30;
  const d = new Date(dob); if (isNaN(d)) return 30;
  const t = new Date(); let a = t.getFullYear() - d.getFullYear();
  const m = t.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && t.getDate() < d.getDate())) a--;
  return a;
}

export function annualTax(annual, age, cfg) {
  cfg = cfgOf(cfg);
  const br = cfg.brackets; let b = br[br.length - 1];
  for (let i = 0; i < br.length; i++) { if (annual <= br[i].upTo) { b = br[i]; break; } }
  const tax = b.base + (annual - b.over) * b.rate;
  const r = cfg.rebates;
  const rebate = (r.primary || 0) + (age >= 65 ? (r.secondary || 0) : 0) + (age >= 75 ? (r.tertiary || 0) : 0);
  return Math.max(0, tax - rebate);
}

export function paye(periodTaxable, frequency, age, cfg) {
  const ppy = PERIODS[frequency] || 12;
  return round2(annualTax((Number(periodTaxable) || 0) * ppy, age, cfg) / ppy);
}

export function uif(periodGross, frequency, cfg) {
  cfg = cfgOf(cfg);
  const ppy = PERIODS[frequency] || 12;
  const periodCeiling = (cfg.uifCeiling * 12) / ppy;
  return round2(Math.min(Number(periodGross) || 0, periodCeiling) * cfg.uifRate);
}

export function monthlyMTC(members, cfg) {
  cfg = cfgOf(cfg); members = Number(members) || 0;
  if (members <= 0) return 0;
  const m = cfg.mtc; let c = (m.main || 0);
  if (members >= 2) c += (m.firstDep || 0);
  if (members > 2) c += (members - 2) * (m.additional || 0);
  return c;
}

// opts: { gross, allowance, frequency, age|dob, uifApplicable, sdlApplicable,
//         otherDeduction, pension, medicalMembers, payeOverride, cfg }
export function computePayslip(opts) {
  const cfg = cfgOf(opts.cfg);
  const ppy = PERIODS[opts.frequency] || 12;
  const gross = round2((Number(opts.gross) || 0) + (Number(opts.allowance) || 0));
  const pension = round2(opts.pension);
  const age = (opts.age != null) ? opts.age : ageFrom(opts.dob);
  const taxable = Math.max(0, round2(gross - pension)); // pension reduces taxable income
  const mtcPeriod = round2(monthlyMTC(opts.medicalMembers, cfg) * 12 / ppy);
  let payeAmt;
  if (opts.payeOverride != null && opts.payeOverride !== "") {
    payeAmt = round2(opts.payeOverride);
  } else {
    payeAmt = Math.max(0, round2(paye(taxable, opts.frequency, age, cfg) - mtcPeriod));
  }
  const uifEmp = (opts.uifApplicable === false) ? 0 : uif(gross, opts.frequency, cfg);
  const uifEr = uifEmp;
  const sdl = opts.sdlApplicable ? round2(gross * cfg.sdlRate) : 0;
  const otherDed = round2(opts.otherDeduction);
  const net = round2(gross - pension - payeAmt - uifEmp - otherDed);
  return { gross, pension, mtc: mtcPeriod, paye: payeAmt, uifEmp, uifEr, sdl, otherDeduction: otherDed, net, age };
}
